import React, { useState, useEffect } from 'react';
import { Table, Select, Space, Button, Avatar, Modal, Descriptions, Form, Input, message } from 'antd';
import { UserOutlined, EyeOutlined, FileTextOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import './ConnectedUsers.css';

const { Option } = Select;

const ConnectedUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('/backend/get_users.php', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des utilisateurs');
        }

        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Erreur:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleViewDetails = (record) => {
    fetchUserDetails(record.recordNumber).then((userDetails) => {
      setSelectedUser(userDetails);
      setIsModalVisible(true);
    });
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await fetch('/backend/get_users.php', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des détails de l\'utilisateur');
      }

      const data = await response.json();
      const user = data.find((u) => u.recordNumber === userId);
      return user || {};
    } catch (error) {
      console.error('Erreur:', error);
      return {};
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch('/backend/delete_user.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: selectedUser.recordNumber }),
      });

      const result = await response.json();

      if (response.ok) {
        setUsers(users.filter((user) => user.recordNumber !== selectedUser.recordNumber));
        setIsModalVisible(false);
        message.success('Utilisateur supprimé avec succès');
      } else {
        message.error(result.error || 'Erreur lors de la suppression de l\'utilisateur');
      }
    } catch (error) {
      message.error('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleEdit = () => {
    form.setFieldsValue({
      first_name: selectedUser.name.split(' ')[0] || '',
      last_name: selectedUser.name.split(' ').slice(1).join(' ') || '',
      email: selectedUser.email || '',
      zip_code: selectedUser.zip || '',
      city: selectedUser.location || '',
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      const updatedUser = {
        id: selectedUser.recordNumber,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        zip_code: values.zip_code,
        city: values.city,
      };

      const response = await fetch('/backend/update_user.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updatedUser),
      });

      const result = await response.json();

      if (response.ok) {
        setUsers(
          users.map((user) =>
            user.recordNumber === selectedUser.recordNumber
              ? {
                  ...user,
                  name: `${values.first_name} ${values.last_name}`.trim() || user.email,
                  zip: values.zip_code,
                  location: values.city,
                }
              : user
          )
        );
        setIsEditModalVisible(false);
        setIsModalVisible(false);
        message.success('Utilisateur mis à jour avec succès');
      } else {
        message.error(result.error || 'Erreur lors de la mise à jour de l\'utilisateur');
      }
    } catch (error) {
      message.error('Erreur lors de la mise à jour de l\'utilisateur');
    }
  };

  const columns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} className="user-avatar" />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Record Number',
      dataIndex: 'recordNumber',
      key: 'recordNumber',
    },
    {
      title: 'ZIP',
      dataIndex: 'zip',
      key: 'zip',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Button type="default" className="status-btn">
          {status}
        </Button>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            className="action-btn view-btn"
            onClick={() => handleViewDetails(record)}
          >
            View Details
          </Button>
          <Button
            icon={<FileTextOutlined />}
            className="action-btn report-btn"
            onClick={() => alert('Report Issue clicked! (Placeholder action)')}
          >
            Report Issue
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="connected-users-container">
      <div className="table-header">
        <Select defaultValue="5" style={{ width: 100 }} onChange={() => {}}>
          <Option value="5">5</Option>
          <Option value="10">10</Option>
          <Option value="20">20</Option>
          <Option value="50">50</Option>
        </Select>
        <span>entries</span>
      </div>
      <Table
        columns={columns}
        dataSource={users}
        pagination={false}
        className="table-style"
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
        loading={loading}
      />
      <Modal
        title="Détails de l'utilisateur"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="delete" icon={<DeleteOutlined />} danger onClick={handleDelete}>
            Supprimer
          </Button>,
          <Button key="edit" icon={<EditOutlined />} type="primary" onClick={handleEdit}>
            Modifier
          </Button>,
        ]}
      >
        {selectedUser && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{selectedUser.recordNumber}</Descriptions.Item>
            <Descriptions.Item label="Nom">{selectedUser.name}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedUser.email || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Code postal">{selectedUser.zip}</Descriptions.Item>
            <Descriptions.Item label="Ville">{selectedUser.location}</Descriptions.Item>
            <Descriptions.Item label="Statut">{selectedUser.status}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      <Modal
        title="Modifier l'utilisateur"
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handleUpdate} layout="vertical">
          <Form.Item
            label="Prénom"
            name="first_name"
            rules={[{ required: true, message: 'Veuillez entrer le prénom' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Nom de famille"
            name="last_name"
            rules={[{ required: true, message: 'Veuillez entrer le nom de famille' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, message: 'Veuillez entrer l\'email' }, { type: 'email', message: 'Email invalide' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Code postal" name="zip_code">
            <Input />
          </Form.Item>
          <Form.Item label="Ville" name="city">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConnectedUsers;